import { updatePaginationState } from "@/store/paginationStore";
import { Button } from "@/components/ui/button";
import { RefreshCcwIcon } from "lucide-react";
import { PaginatedSocialSecurityQueryVariables, SocialSecurityFilters } from "@/graphql/__generated__/graphql";
import { SocialSecurityDateRange, SocialSecuritySortingDropdown, SocialSecurityValueSearch } from "./Components";

interface Props {
  onFilterSubmit: (_variables: PaginatedSocialSecurityQueryVariables) => void;
  displayAmount: number;
}

/**
 * SocialSecurityFilter component handles filtering and sorting of social security
 * It manages filter state and provides UI for filter controls
 * @param props Component properties
 * @returns JSX element
 */
const SocialSecurityFilter = (props: Props) => {
  const { onFilterSubmit, displayAmount } = props;

  /**
   * Handles filter submission
   * @param newValues New filter values to submit
   */
  const handleSubmitClick = (newValues: PaginatedSocialSecurityQueryVariables) => {
    onFilterSubmit(newValues);
  };

  /**
   * Resets all filter values and sort settings
   * Updates both local state and triggers query update
   */
  const handleResetClickFilters = () => {
    // Define empty filters object
    const emptyFilters = {
      activeFrom: null,
      value: null,
    };

    // Reset all filter values and sort in the global store
    updatePaginationState({
      paginatedSocialSecurity: {
        filters: emptyFilters as SocialSecurityFilters,
        sort: null
      }
    });

    // Create reset query variables with explicitly empty filters and sort
    const updatedVariables: PaginatedSocialSecurityQueryVariables = {
      first: displayAmount,
      filters: {
        activeFrom: null,
        value: null,
      },
      sort: null
    };

    // Immediately submit the reset filter state to parent component
    onFilterSubmit(updatedVariables);
  };

  return (
    <div className="flex flex-1 gap-2">
      <div className="flex flex-1 items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Date Range - Controls date filter */}
          <SocialSecurityDateRange
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Value Search - Controls value filter */}
          <SocialSecurityValueSearch
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Sorting Dropdown - Controls sort field and direction */}
          <SocialSecuritySortingDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />
        </div>
        <div>
          <Button variant="outline" size="icon" onClick={handleResetClickFilters}>
            <RefreshCcwIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SocialSecurityFilter;
